import { collection, query, where, doc, getDoc, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { normalizeText, tokenizeName, levenshteinDistance } from '../utils/textUtils';
import { calculateUnifiedScore, checkDistributedMatches } from '../utils/scoringUtils';
import { generateVerificationId } from '../utils/verificationUtils';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { checkSearchLimit, incrementSearchUsage } from './usageService';

// Constants
const MIN_SCORE_THRESHOLD = 45; // Minimum score to include in results (Low confidence and above)
const VECTOR_SEARCH_LIMIT = 20; // Limit results from Vector AI search
const ENDPOINT = 'https://discoveryengine.googleapis.com/v1alpha/projects/465515383750/locations/global/collections/default_collection/engines/sanction-search_1744728610112/servingConfigs/default_search:search';
const LANGUAGE_CODE = 'en-US';
const TIME_ZONE = 'Europe/London';

/**
 * Warm up the authentication token proactively
 * This can be called when a user logs in to prepare for searches
 * @returns {Promise<boolean>} True if token was warmed successfully
 */
export async function warmAuthToken() {
  try {
    console.log('Warming up authentication token...');
    const functions = getFunctions();
    const warmToken = httpsCallable(functions, 'warmGcloudToken');
    
    const result = await warmToken();
    const data = result.data;
    
    if (data.success) {
      console.log('Authentication token warmed successfully:', data.message);
      return true;
    } else {
      console.warn('Token warming failed:', data);
      return false;
    }
  } catch (error) {
    console.warn('Error warming authentication token:', error);
    // Don't throw - this is a performance optimization, not critical
    return false;
  }
}

/**
 * Smart token warming - only warms if not recently warmed
 * Uses localStorage to track last warming time
 * @returns {Promise<boolean>} True if token was warmed successfully or recently warmed
 */
export async function smartWarmAuthToken() {
  try {
    const lastWarmTime = localStorage.getItem('lastTokenWarm');
    const now = Date.now();
    const warmInterval = 30 * 60 * 1000; // 30 minutes
    
    if (lastWarmTime && (now - parseInt(lastWarmTime)) < warmInterval) {
      console.log('Token recently warmed, skipping...');
      return true;
    }
    
    const success = await warmAuthToken();
    if (success) {
      localStorage.setItem('lastTokenWarm', now.toString());
    }
    return success;
  } catch (error) {
    console.warn('Error in smart token warming:', error);
    return false;
  }
}

/**
 * Get an authentication token using Firebase Functions
 * @returns {Promise<string>} The access token
 */
async function getAccessToken() {
  try {
    // Use Firebase Functions to get the token
    const functions = getFunctions();
    const getGcloudToken = httpsCallable(functions, 'getGcloudToken');
    
    const result = await getGcloudToken();
    const data = result.data;
    
    if (!data.token) {
      throw new Error('Token function returned empty token');
    }
    
    return data.token;
  } catch (error) {
    console.error('Error getting access token:', error);
    throw error;
  }
}

/**
 * Call the Vector AI search API to get initial matches
 * @param {string} query - The search query
 * @param {string} entityType - Optional entity type filter ('person' or 'enterprise')
 * @returns {Promise<Array>} Array of search results
 */
async function callVectorSearch(query, entityType = null) {
  try {
    const accessToken = await getAccessToken();
    
    if (!accessToken) {
      throw new Error("Failed to obtain a valid access token");
    }
    
    const payload = {
      query,
      pageSize: VECTOR_SEARCH_LIMIT,
      queryExpansionSpec: { condition: 'AUTO' },
      spellCorrectionSpec: { mode: 'AUTO' },
      languageCode: LANGUAGE_CODE,
      userInfo: { timeZone: TIME_ZONE }
    };
    
    const headers = {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    };
    
    console.log('Calling Vector API with query:', query);
    
    const response = await fetch(ENDPOINT, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload)
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Vector API error (HTTP ${response.status}):`, errorText);
      throw new Error(`Vector search API error: ${response.status}`);
    }
    
    let data;
    try {
      data = await response.json();
    } catch (parseError) {
      console.error('Failed to parse Vector API response as JSON:', parseError);
      throw new Error('Invalid Vector API response format');
    }
    
    if (!data) {
      throw new Error('Empty response from Vector API');
    }
    
    const results = data.results || [];
    console.log(`Vector API returned ${results.length} results`);
    
    // Apply entity type filtering if specified
    if (entityType && results.length > 0) {
      const filteredResults = results.filter(res => {
        const structData = res.document?.structData;
        return structData && structData.entityType === entityType;
      });
      
      console.log(`Filtered to ${filteredResults.length} results matching entity type: ${entityType}`);
      return filteredResults;
    }
    
    return results;
  } catch (error) {
    console.error('Vector search error:', error);
    throw error;
  }
}

/**
 * Map Vector AI results to Firestore document IDs and fetch the full documents
 * @param {Array} vectorResults - Results from Vector AI search
 * @returns {Promise<Object>} Object containing array of Firestore documents with IDs and count of inactive records skipped
 */
async function fetchFirestoreDocumentsFromVectorResults(vectorResults) {
  // Extract unique document IDs from vector results
  const documentIdentifiers = new Map();
  
  vectorResults.forEach(result => {
    const structData = result.document?.structData;
    if (structData) {
      const source = structData.source;
      const originalId = structData.originalId;
      
      if (source && originalId) {
        // Use originalId directly as it already contains the source prefix
        const docId = originalId;
        
        // Store the document ID with its vector result for reference
        if (!documentIdentifiers.has(docId)) {
          documentIdentifiers.set(docId, {
            docId,
            source,
            originalId,
            vectorResult: result
          });
        }
      }
    }
  });
  
  console.log(`Extracted ${documentIdentifiers.size} unique document IDs from ${vectorResults.length} vector results`);
  
  // Fetch the full documents from Firestore
  const sanctionsRef = collection(db, 'sanctions');
  const documents = [];
  let inactiveRecordsSkipped = 0;
  
  // Process in batches to avoid overwhelming Firestore
  const batches = Array.from(documentIdentifiers.values()).reduce((acc, curr, i) => {
    const batchIndex = Math.floor(i / 10);
    if (!acc[batchIndex]) acc[batchIndex] = [];
    acc[batchIndex].push(curr);
    return acc;
  }, []);
  
  for (const batch of batches) {
    const batchPromises = batch.map(async ({ docId }) => {
      const docRef = doc(sanctionsRef, docId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        
        // Skip inactive sanctions records
        if (data.status === 'inactive') {
          console.log(`Skipping inactive sanction record: ${docId}`);
          inactiveRecordsSkipped++;
          return null;
        }
        
        return {
          id: docSnap.id,
          data: data
        };
      }
      return null;
    });
    
    const batchResults = await Promise.all(batchPromises);
    documents.push(...batchResults.filter(Boolean));
  }
  
  console.log(`Found ${documents.length} active sanctions documents from ${documentIdentifiers.size} vector results`);
  if (inactiveRecordsSkipped > 0) {
    console.log(`Skipped ${inactiveRecordsSkipped} inactive sanction records`);
  }
  
  return {
    documents,
    inactiveRecordsSkipped
  };
}

/**
 * Vector-based search function that uses Vertex AI for first stage and detailed scoring for second stage
 * @param {string} searchTerm - The search term to look for
 * @param {Function} updateProgress - Callback to update search progress
 * @param {string} userId - The ID of the user performing the search (optional)
 * @param {string} entityType - Type of entity to search for ('person' or 'enterprise', null for both)
 * @param {string} orgId - The ID of the organization performing the search (optional)
 * @returns {Object} Search results and statistics
 */
export const vectorSearchSanctions = async (searchTerm, updateProgress, userId = null, entityType = null, orgId = null) => {
  try {
    console.time('vectorSearch');
    
    // Check search limit before performing the search
    if (orgId) {
      try {
        const limitCheck = await checkSearchLimit(orgId);
        if (!limitCheck.allowed) {
          console.error("Search limit exceeded:", limitCheck);
          const error = new Error(`Monthly search limit exceeded. You have used ${limitCheck.used} of ${limitCheck.totalLimit} searches this month.`);
          error.code = 'SEARCH_LIMIT_EXCEEDED';
          error.limitInfo = limitCheck;
          throw error;
        }
        console.log(`Search limit check passed for organization ${orgId}`);
      } catch (limitError) {
        console.error("Search limit check failed:", limitError);
        throw limitError;
      }
    }
    
    // Generate a timestamp and verification ID for the search
    const searchTimestamp = Date.now();
    const verificationId = await generateVerificationId(searchTerm, searchTimestamp);
    
    // Normalize and tokenize the search term
    const searchTermNormalized = normalizeText(searchTerm);
    const searchTermTokens = tokenizeName(searchTerm);
    
    // Stats tracking
    const stats = {
      totalDocumentsQueried: 0,
      totalNamesProcessed: 0,
      firstStageFiltered: 0,
      secondStageMatches: 0,
      inactiveRecordsSkipped: 0,
      totalProcessingTime: 0,
      entityTypeFilter: entityType || 'all'
    };
    
    console.log(`Vector searching for: "${searchTerm}" (tokens: ${searchTermTokens.join(', ')}), entity type: ${entityType || 'all'}`);
    
    // Update progress for vector search stage
    if (updateProgress) {
      updateProgress({
        processed: 0,
        total: 100,
        stage: 'vector'
      });
    }
    
    try {
      // FIRST STAGE: Use Vector AI search to get initial matches
      const vectorResults = await callVectorSearch(searchTerm, entityType);
      
      if (updateProgress) {
        updateProgress({
          processed: 50,
          total: 100,
          stage: 'vector'
        });
      }
      
      console.log(`Vector search returned ${vectorResults.length} initial matches`);
      
      // Map vector results to Firestore documents and fetch them
      const { documents, inactiveRecordsSkipped } = await fetchFirestoreDocumentsFromVectorResults(vectorResults);
      
      stats.firstStageFiltered = documents.length;
      stats.totalDocumentsQueried = vectorResults.length;
      stats.inactiveRecordsSkipped = inactiveRecordsSkipped;
      
      console.log(`First stage complete: Found ${stats.firstStageFiltered} Firestore documents from vector search`);
      
      // Update progress for transition
      if (updateProgress) {
        updateProgress({
          processed: 100,
          total: 100,
          stage: 'vector'
        });
        
        // Add a small delay to show the completion of the first stage
        await new Promise(resolve => setTimeout(resolve, 200));
        
        // Update for second stage preparation
        updateProgress({
          processed: 0,
          total: documents.length || 1,
          stage: 'preparing'
        });
        
        // Another small delay to show the transition
        await new Promise(resolve => setTimeout(resolve, 300));
        
        // Update for second stage
        updateProgress({
          processed: 0,
          total: documents.length || 1,
          stage: 'second'
        });
      }
      
      // SECOND STAGE: Detailed matching on potential matches (similar to optimizedSearchService)
      const matchingResults = [];
      let processedCount = 0;
      
      // Process potential matches with detailed scoring
      for (const { id, data } of documents) {
        processedCount++;
        
        // Update progress periodically
        if (updateProgress && processedCount % 5 === 0) {
          updateProgress({
            processed: processedCount,
            total: documents.length,
            stage: 'second'
          });
        }
        
        // Skip documents with no names
        if (!data.names || !data.names.length) {
          continue;
        }
        
        // Check for distributed matches
        const distributedMatch = checkDistributedMatches(data, searchTermTokens);
        
        // Create a base record with common information
        const baseRecord = {
          id,
          docData: data,
          remark: data.remark || '',
          designationDetails: data.designationDetails || '',
          subjectType: data.subjectType?.code || '',
          citizenships: (data.citizenships || []).map(c => c.countryDescription).join(', '),
          birthdates: (data.birthdates || []).map(b => b.birthdate || 
            `${b.year || ''}${b.year && (b.dayOfMonth || b.monthOfYear) ? '-' : ''}${b.monthOfYear || ''}${b.monthOfYear && b.dayOfMonth ? '-' : ''}${b.dayOfMonth || ''}`
          ).filter(Boolean).join(', '),
          isDistributedMatch: distributedMatch.isDistributedMatch,
          distributionInfo: distributedMatch.distribution,
          matchedTokens: distributedMatch.matchedTokens
        };
        
        // Track the best match for this document
        let bestMatch = null;
        let bestScore = 0;
        
        // Process each name within the document
        data.names.forEach((name) => {
          stats.totalNamesProcessed++;
          
          // Get full name
          const constructedName = `${name.title || ''} ${name.firstName || ''} ${name.middleName || ''} ${name.lastName || ''}`.trim();
          const fullName = name.wholeName ? name.wholeName : constructedName;
          
          if (!fullName) return;
          
          // Use the pre-computed normalized name if available
          const normalizedName = name.normalizedName || normalizeText(fullName);
          
          const nameEntry = {
            ...baseRecord,
            nameIndex: data.names.indexOf(name),
            fullName: fullName,
            normalizedName: normalizedName,
            firstName: name.firstName || '',
            middleName: name.middleName || '',
            lastName: name.lastName || '',
            gender: name.gender || '',
            function: name.function || '',
            title: name.title || '',
            strong: name.strong || false,
            qualityLevel: name.qualityLevel || 'UNKNOWN',
            qualityScore: name.qualityScore || 0,
            qualityFlags: name.qualityFlags || []
          };
          
          // Calculate unified score using the existing algorithm
          const score = calculateUnifiedScore(nameEntry, searchTermTokens, searchTermNormalized);
          nameEntry.unifiedScore = score;
          
          // Set confidence level
          if (score >= 95) {
            nameEntry.confidenceLevel = "Very High";
          } else if (score >= 80) {
            nameEntry.confidenceLevel = "High";
          } else if (score >= 65) {
            nameEntry.confidenceLevel = "Medium";
          } else if (score >= 45) {
            nameEntry.confidenceLevel = "Low";
          } else {
            nameEntry.confidenceLevel = "Very Low";
          }
          
          // Track best matching name variant
          if (score > bestScore) {
            bestScore = score;
            bestMatch = nameEntry;
          }
        });
        
        // If we have a match above threshold, add to results
        if (bestMatch && bestMatch.unifiedScore >= MIN_SCORE_THRESHOLD) {
          matchingResults.push(bestMatch);
        }
      }
      
      // Process distributed matches separately to ensure they're not filtered out
      const distributedMatches = [];

      for (const { id, data } of documents) {
        // Skip documents with no names or just one name (can't have distributed match)
        if (!data.names || data.names.length <= 1) {
          continue;
        }
        
        // Check for distributed matches
        const distributedMatch = checkDistributedMatches(data, searchTermTokens);
        if (!distributedMatch.isDistributedMatch) {
          continue;
        }
        
        // Find the best name variant for this distributed match
        let bestNameEntry = null;
        let bestScore = 0;
        
        data.names.forEach((name) => {
          // Get full name
          const constructedName = `${name.title || ''} ${name.firstName || ''} ${name.middleName || ''} ${name.lastName || ''}`.trim();
          const fullName = name.wholeName ? name.wholeName : constructedName;
          if (!fullName) return;
          
          // Create a name entry for scoring
          const nameEntry = {
            id,
            docData: data,
            nameIndex: data.names.indexOf(name),
            fullName: fullName,
            normalizedName: normalizeText(fullName),
            remark: data.remark || '',
            designationDetails: data.designationDetails || '',
            subjectType: data.subjectType?.code || '',
            citizenships: (data.citizenships || []).map(c => c.countryDescription).join(', '),
            birthdates: (data.birthdates || []).map(b => b.birthdate || 
              `${b.year || ''}${b.year && (b.dayOfMonth || b.monthOfYear) ? '-' : ''}${b.monthOfYear || ''}${b.monthOfYear && b.dayOfMonth ? '-' : ''}${b.dayOfMonth || ''}`
            ).filter(Boolean).join(', '),
            isDistributedMatch: true,
            distributionInfo: distributedMatch.distribution,
            matchedTokens: distributedMatch.matchedTokens
          };
          
          // Calculate base score
          const score = calculateUnifiedScore(nameEntry, searchTermTokens, searchTermNormalized);
          
          // Apply distributed match boost
          const distributedBoost = 25; // Higher boost since all name components are important
          const boostedScore = Math.min(100, score + distributedBoost);
          
          nameEntry.unifiedScore = boostedScore;
          nameEntry.distributedBoost = distributedBoost;
          
          // Set confidence level
          if (boostedScore >= 95) {
            nameEntry.confidenceLevel = "Very High";
          } else if (boostedScore >= 80) {
            nameEntry.confidenceLevel = "High";
          } else if (boostedScore >= 65) {
            nameEntry.confidenceLevel = "Medium";
          } else if (boostedScore >= 45) {
            nameEntry.confidenceLevel = "Low";
          } else {
            nameEntry.confidenceLevel = "Very Low";
          }
          
          // Track best match
          if (boostedScore > bestScore) {
            bestScore = boostedScore;
            bestNameEntry = nameEntry;
          }
        });
        
        // Add distributed matches with scores above threshold
        if (bestNameEntry && bestNameEntry.unifiedScore >= MIN_SCORE_THRESHOLD) {
          distributedMatches.push(bestNameEntry);
        }
      }
      
      // Add distributed matches to results
      matchingResults.push(...distributedMatches);
      
      // Sort results by score (highest first)
      matchingResults.sort((a, b) => b.unifiedScore - a.unifiedScore);
      
      stats.secondStageMatches = matchingResults.length;
      
      // Limit results to reasonable number
      let limitedResults = matchingResults;
      if (matchingResults.length > 200) {
        console.log(`Limited ${matchingResults.length} results to top 200 by score`);
        limitedResults = matchingResults.slice(0, 200); // Limit to top 200 scores
      }
      
      // Group results by confidence level for stats
      const veryHighConfidence = matchingResults.filter(r => r.unifiedScore >= 95);
      const highConfidence = matchingResults.filter(r => r.unifiedScore >= 80 && r.unifiedScore < 95);
      const mediumConfidence = matchingResults.filter(r => r.unifiedScore >= 65 && r.unifiedScore < 80);
      const lowConfidence = matchingResults.filter(r => r.unifiedScore >= 45 && r.unifiedScore < 65);
      
      // Final stats
      const searchStats = {
        ...stats,
        totalMatches: matchingResults.length,
        veryHighConfidence: veryHighConfidence.length,
        highConfidence: highConfidence.length,
        mediumConfidence: mediumConfidence.length,
        lowConfidence: lowConfidence.length,
        distributedMatches: matchingResults.filter(r => r.isDistributedMatch).length,
        verificationId: verificationId,
        searchTime: new Date().toISOString()
      };
      
      console.timeEnd('vectorSearch');
      
      // Calculate total processing time
      searchStats.totalProcessingTime = Date.now() - searchTimestamp;
      
      console.log(`Vector search complete: ${searchStats.totalMatches} matches found in ${searchStats.totalProcessingTime}ms`);
      console.log(`First stage filtered ${searchStats.firstStageFiltered} out of ${searchStats.totalDocumentsQueried} documents`);
      if (searchStats.inactiveRecordsSkipped > 0) {
        console.log(`Skipped ${searchStats.inactiveRecordsSkipped} inactive sanctions records`);
      }
      
      // Increment search usage after successful search
      if (orgId) {
        try {
          await incrementSearchUsage(orgId, 1);
          console.log(`Incremented search usage for organization ${orgId}`);
        } catch (usageError) {
          console.error("Error incrementing search usage:", usageError);
          // Don't fail the search if usage increment fails
        }
      }
      
      return {
        results: limitedResults,
        stats: searchStats
      };
      
    } catch (vectorError) {
      // If vector search fails, we'll fall back to the optimized search
      console.error('Vector search failed, falling back to optimized search:', vectorError);
      
      // Import the optimized search function
      const { optimizedSearchSanctions } = await import('./optimizedSearchService');
      
      if (updateProgress) {
        updateProgress({
          processed: 0,
          total: 100,
          stage: 'first' // Switch back to the stages used by optimized search
        });
      }
      
      // Fall back to optimized search  
      return await optimizedSearchSanctions(searchTerm, updateProgress, userId, entityType);
    }
    
  } catch (error) {
    console.error('Error in vector search:', error);
    throw error;
  }
};

/**
 * Export the optimized search function to maintain compatibility
 */
export { optimizedSearchSanctions } from './optimizedSearchService'; 